// Small pieces the signature screens share: the request and signer status chips (a word and an icon,
// colour is never alone), the SHA-256 shortened with a copy button, a date kept left to right and
// the error bar.
import { formatDate, formatDateTime } from '@batiflow/i18n';
import { StatusChip } from '@batiflow/ui';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  __css,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircle16Regular,
  Clock16Regular,
  Copy16Regular,
  DismissCircle16Regular,
  Prohibited16Regular,
  Warning16Regular,
} from '@fluentui/react-icons';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { useNotify } from '@/shared/hooks/use-notify';
import {
  SIGNER_INTENT,
  STATUS_INTENT,
  ltr,
  shortHash,
  type SignatureRequestStatus,
  type Signer,
} from './model';

const useStyles = __css({"hash":{"mc9l5x":"ftuwxu6","Bt984gj":"f122n59","i8kkvl":"f1ufnopg","Bgto7am":"fuzgk9b"},"mono":{"Bahqtrf":"f1fedwem","Be2twd7":"fy9rknc","Btd35i7":"fjksvth"},"date":{"B7salou":"f1pbl6dw"}});

const STATUS_ICON: Readonly<Record<SignatureRequestStatus, ReactElement>> = {
  pending: <Clock16Regular />,
  completed: <CheckmarkCircle16Regular />,
  declined: <DismissCircle16Regular />,
  cancelled: <Prohibited16Regular />,
  expired: <Warning16Regular />,
};

export function RequestStatusChip({ status }: { status: SignatureRequestStatus }) {
  const { t } = useTranslation('signatures');
  return (
    <StatusChip
      intent={STATUS_INTENT[status]}
      icon={STATUS_ICON[status]}
      label={t(`statuses.${status}`)}
    />
  );
}

const SIGNER_ICON: Readonly<Record<Signer['status'], ReactElement>> = {
  pending: <Clock16Regular />,
  signed: <CheckmarkCircle16Regular />,
  declined: <DismissCircle16Regular />,
};

export function SignerStatusChip({ status }: { status: Signer['status'] }) {
  const { t } = useTranslation('signatures');
  return (
    <StatusChip
      intent={SIGNER_INTENT[status]}
      icon={SIGNER_ICON[status]}
      label={t(`signerStatuses.${status}`)}
    />
  );
}

/** Dates as text kept left to right (LRI … PDI), to put inside a translated sentence. */
export function useWhen(): { day: (iso: string) => string; moment: (iso: string) => string } {
  const locale = useLocale();
  return {
    day: (iso) => ltr(formatDate(iso, locale)),
    moment: (iso) => ltr(formatDateTime(iso, locale)),
  };
}

/** A date or a date and time that keeps its order inside an Arabic sentence. */
export function Moment({ iso, time = false }: { iso: string; time?: boolean }) {
  const styles = useStyles();
  const locale = useLocale();
  return (
    <bdi dir="ltr" className={styles.date}>
      {time ? formatDateTime(iso, locale) : formatDate(iso, locale)}
    </bdi>
  );
}

/** The first and last characters of a SHA-256, the whole value on hover and in the copy button's name. */
export function HashValue({ hash }: { hash: string }) {
  const { t } = useTranslation('signatures');
  const styles = useStyles();
  const notify = useNotify();
  const copy = () => {
    if (typeof navigator.clipboard === 'undefined') {
      notify({ intent: 'error', message: t('hash.copyFailed') });
      return;
    }
    navigator.clipboard.writeText(hash).then(
      () => {
        notify({ intent: 'success', message: t('hash.copied') });
      },
      () => {
        notify({ intent: 'error', message: t('hash.copyFailed') });
      },
    );
  };
  return (
    <span className={styles.hash}>
      <bdi dir="ltr" className={styles.mono} title={hash}>
        {shortHash(hash)}
      </bdi>
      <Button
        size="small"
        appearance="subtle"
        icon={<Copy16Regular />}
        aria-label={t('hash.copy', { hash })}
        onClick={copy}
      />
    </span>
  );
}

export function ErrorBar({
  title,
  body,
  onRetry,
}: {
  title: string;
  body: string;
  onRetry?: () => void;
}) {
  const { t } = useTranslation('common');
  return (
    <MessageBar intent="error" layout="multiline">
      <MessageBarBody>
        <MessageBarTitle>{title}</MessageBarTitle>
        {body}
      </MessageBarBody>
      {onRetry ? (
        <MessageBarActions>
          <Button onClick={onRetry}>{t('actions.retry')}</Button>
        </MessageBarActions>
      ) : null}
    </MessageBar>
  );
}


import "/opt/batiflow/app/apps/web/src/features/signatures/bits.griffel.css";